import { Injectable } from '@nestjs/common';
import { Repository } from 'typeorm';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { InjectRepository } from '@nestjs/typeorm';
import { Education } from './education.entity';
import { EducationDTO } from './dto/education.dto';
import { EducationAdapter } from './dto/education.adapater';
import { EducationIdRequest } from './dto/education.request';
import { AllEducationResponseModel, EducationResponseModel } from '@gtpl/shared-models/hrms';

@Injectable()
export class EducationsService {

    constructor(
        @InjectRepository(Education)
        private repository: Repository<Education>,
        private adapter: EducationAdapter,
    ){}

    async getEducationWithoutRelations(education: string): Promise<Education>{
        const response = await this.repository.findOne({
            where: {education: education},
        });
        if(response){
            return response;
        }else {
            return null;
        }
    }

    async createEducation(dto: EducationDTO, isUpdate: boolean): Promise<EducationResponseModel>{
        try{
            if(!isUpdate){
                const entity =  await this.getEducationWithoutRelations(dto.education);
                if(entity){
                    throw new ErrorResponse(11104, 'Education already exists');
                }
            }else{
                const entity = await this.getEducationWithoutRelations(dto.education);
                if(entity){
                    if(entity.educationId != dto.educationId){
                        throw new ErrorResponse(11104, 'Education already exists');
                    }
                }
            }
            const convertedDto: Education = this.adapter.convertDtoToEntity(dto);
            const savedEntity: Education = await this.repository.save(convertedDto);
            const savedDto: EducationDTO = this.adapter.convertEntityToDto(savedEntity);
            if(savedDto){
                const response = new EducationResponseModel(true, 1, isUpdate? 'Education updated successfully': 'Education created successfully', savedDto);
                return response;
            }else{
                throw new ErrorResponse(11106, 'Education saved but there is an issue while converting it into DTO');
            }
        }catch(error){
            throw error;
        }
    }

    async getAllEducations(): Promise<AllEducationResponseModel>{
        try{
            const dtos: EducationDTO[] = [];
            const entities: Education[] = await this.repository.find({order: {education: 'ASC'}});
            if(entities){
                entities.forEach(entity => {
                    const Dto: Education = this.adapter.convertEntityToDto(entity);
                    dtos.push(Dto);
                });
                const response = new AllEducationResponseModel(true, 1, 'Educations Retrieved Successfully', dtos);
                return response;
            }else{
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        }catch(error){
            throw error;
        }
    }

    async getAllActiveEducations(): Promise<AllEducationResponseModel>{
        try{
            const dtos: EducationDTO[] = [];
            const entities: Education[] = await this.repository.find({order: {education: 'ASC'}, where: {isActive: true}});
            if(entities){
                entities.forEach(entity => {
                    const Dto: Education = this.adapter.convertEntityToDto(entity);
                    dtos.push(Dto);
                });
                const response = new AllEducationResponseModel(true, 1, 'Education Retrieved Successfully', dtos);
                return response;
            }else{
                throw new ErrorResponse(99998, 'Data Not Found');
            }
        }catch(error){
            throw error;
        }
    }

    async getEducationByEduId(id: number): Promise<Education>{
        const response = await this.repository.findOne({where: {educationId: id}});
        if(response){
            return response;
        }else{
            return null;
        }
    }

    async activateOrDeactivateEducation(req: EducationIdRequest): Promise<EducationResponseModel>{
        try{
            const certExists = await this.getEducationByEduId(req.educationId);
            if(certExists){
                if(req.versionFlag !=  certExists.versionFlag){
                    throw new ErrorResponse(10113, 'Someone updated the current Education informations. Refresh and try again');                    
                }else{
                    const certStatus = await this.repository.update(
                        {educationId: req.educationId},
                        {isActive: req.isActive, updatedUser: req.updatedUser}
                    );
                    if(certExists.isActive){
                        if(certStatus.affected){
                            const response: EducationResponseModel = new EducationResponseModel(true, 10115, 'Education de-activated successfully');
                            return response;
                        }else{
                            throw new ErrorResponse(10111, 'Education is already de-activated');
                        }
                    }else{
                        if(certStatus.affected){
                            const response: EducationResponseModel = new EducationResponseModel(true, 10114, 'Education activated successfully');
                            return response; 
                        }else{
                            throw new ErrorResponse(10112, 'Education is already activated');
                        }
                    }
                }
            }else{
                throw new ErrorResponse(99998, "No Records Found");
            }
        }catch(error){
            throw error;
        }
    }

    async getEducationById(req: EducationIdRequest): Promise<EducationResponseModel>{
        try{
        const entity: Education = await this.repository.findOne({where: {educationId: req.educationId}});
        if(entity){
            const convertedEntity = this.adapter.convertEntityToDto(entity);
            const response: EducationResponseModel = new EducationResponseModel(true, 1, 'Education found and retrieved', convertedEntity);
            return response;
        }else{
            throw new ErrorResponse(99998, "No Data Found");
        }
        }catch(error){
            throw error;
        }
    }
}
